import { useTranslation } from "react-i18next";
import { ChecklistIcon, InfoIcon } from "../../../../components/icons"
import { MotionModal, ModalCloseButton } from "../../../../components/motion";


// ─── Display helpers ────────────────────────────────────────────────────────
// Every rate and fee is always shown, including 0% and 0 JPY, so the customer
// can see exactly what they were (or were not) charged.

// The backend sends a policy line such as "After 9:00 AM on the day before: 30%".
// Some bands (e.g. business "Free of charge" / 「無料」) omit the number, so the
// percent is appended whenever the text does not already contain it.
const formatRate = (descText, percent) => {
    const text = descText || "";
    const hasPercent = typeof percent === "number";
    if (!hasPercent) return text;
    // Whole-number match, so "0%" is not mistaken as present inside "100%".
    const alreadyShowsPercent = new RegExp(`(^|[^\\d])${percent}%`).test(text);
    if (alreadyShowsPercent) return text;
    return text ? `${text} (${percent}%)` : `${percent}%`;
};

// Missing amounts count as 0 so the row never renders blank.
const toAmount = (value) => Number(value) || 0;

const labelClass = "tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left";
const valueClass = "tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left";

// One "rate | fee" row of the details box.
const RateFeeRow = ({ rateLabel, rateText, feeLabel, feeText, withTopBorder = true }) => (
    <div className={`${withTopBorder ? "tw:border-t tw:border-[#e0e0e0] tw:pt-4 " : ""}tw:flex tw:gap-4 tw:!mb-4`}>
        <div className="tw:flex-1">
            <p className={labelClass}>{rateLabel}</p>
            <p className={valueClass}>{rateText}</p>
        </div>
        <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
            <p className={labelClass}>{feeLabel}</p>
            <p className={valueClass}>{feeText}</p>
        </div>
    </div>
);

const CancelBookingSuccessModal = ({
    show = false,
    onClose = () => { },
    refundAmount = 0,
    cancellationFee = 0,
    cancellationPercent = 0,
    // Both Meet & Assist props arrive per leg: { arrival, departure }.
    meetAssistCancellationPercent = { arrival: 0, departure: 0 },
    meetAssistCancellationFee = { arrival: 0, departure: 0 },
    isLoading = true,
    desc = {
        en: "",
        jp: ""
    },
    meetAssistCancellationDesc = {
        en: "",
        jp: ""
    },
    paymentStatus,
    deferredPayment,
    meetAssist,
    role = "individual",
    totalPrice = 0,
}) => {
    const { t, i18n } = useTranslation();
    const isEnglish = i18n.language === "en";

    const formatJPY = (value) => `${toAmount(value).toLocaleString()}${t("bookingDetail.cancelBookingSuccessModal.jpy")}`;
    const pickLanguage = (text) => (isEnglish ? text?.en : text?.jp);

    const isUsingMeetAssist = (ind = 0) => {
        return meetAssist?.[ind]?.meetAssistPrice > 0;
    }

    // Meet & Assist legs the customer actually booked, with their own rate/fee.
    const meetAssistLegs = [
        { index: 0, key: "arrival", prefix: "arrival" },
        { index: 1, key: "departure", prefix: "departure" },
    ]
        .filter((leg) => isUsingMeetAssist(leg.index))
        .map((leg) => {
            const isVip = meetAssistCancellationDesc?.[leg.key]?.type === "vipMeetAssist";
            const serviceName = isVip ? "VIPMeetAssist" : "MeetAssist";
            const percent = meetAssistCancellationPercent?.[leg.key];
            return {
                ...leg,
                rateLabel: t(`bookingDetail.cancelBookingSuccessModal.${leg.prefix}${serviceName}CancellationRate`),
                feeLabel: t(`bookingDetail.cancelBookingSuccessModal.${leg.prefix}${serviceName}CancellationFee`),
                rateText: formatRate(pickLanguage(meetAssistCancellationDesc?.[leg.key]), percent),
                feeText: formatJPY(meetAssistCancellationFee?.[leg.key]),
                percent: toAmount(percent),
            };
        });

    // Every rate that applies to this booking: the transfer plus each booked
    // Meet & Assist leg. Drives the summary line under the title.
    const appliedPercents = [toAmount(cancellationPercent), ...meetAssistLegs.map((leg) => leg.percent)];

    const getSubTitle = () => {
        if (paymentStatus === "CHARGE_PENDING") {
            if (deferredPayment) {
                return t("bookingDetail.cancelBookingSuccessModal.notIncludedToMonthlyTotal");
            }
            return t("bookingDetail.cancelBookingSuccessModal.noCharge");
        }

        const isNoCancellation = appliedPercents.every((percent) => percent === 0);
        const isFullCancellation = appliedPercents.every((percent) => percent === 100);

        if (isNoCancellation) {
            if (deferredPayment) {
                return t("bookingDetail.cancelBookingSuccessModal.bookingFeeRemovedFromMonthlyTotal");
            }
            return t("bookingDetail.cancelBookingSuccessModal.willRefundShortly");
        }

        if (isFullCancellation) {
            if (deferredPayment) {
                return t("bookingDetail.cancelBookingSuccessModal.feeWillBeIncludedInMonthlyTotal");
            }
            return t("bookingDetail.cancelBookingSuccessModal.fullCancellationFee");
        }

        // Partial cancellation: some fee applies, the rest is refunded / removed.
        if (deferredPayment) {
            return t("bookingDetail.cancelBookingSuccessModal.feeWillBeIncludedInMonthlyTotal");
        }
        return t("bookingDetail.cancelBookingSuccessModal.willRefundShortly");
    };

    const getCancellationPolicyLink = (role) => {
        if (role === "business") {
            return "https://theluxuryfleet.com/corporate-cancellation-policy";
        } else {
            if (i18n.language === "jp") {
                return "https://theluxuryfleet.com/jp/cancellation-policy";
            } else {
                return "https://theluxuryfleet.com/en/cancellation-policy";
            }
        }
    }

    // e.g. "(= 30,000JPY - 9,000JPY - 0JPY)": total minus each fee charged.
    const getRefundAmountDetail = () => {
        const fees = [cancellationFee, ...meetAssistLegs.map((leg) => meetAssistCancellationFee?.[leg.key])];
        return [totalPrice, ...fees].map(formatJPY).join(" - ");
    }

    const subTitle = getSubTitle();

    return (
        <MotionModal
            show={show}
            onClose={onClose}
            maxWidthClassName="tw:max-w-[660px]"
            panelClassName="tw:px-4 tw:py-5 tw:md:px-10 tw:md:py-8"
        >
                <ModalCloseButton onClick={onClose} />

                {
                    isLoading ? (
                        /* --- LOADING STATE --- */
                        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:mt-6">
                            {/* Simple CSS Spinner */}
                            <div className="tw:w-12 tw:h-12 tw:border-4 tw:border-[#f3f3f3] tw:border-t-[#7c7b7b] tw:rounded-full tw:animate-spin tw:mb-6"></div>

                            <p className="tw:!text-[#7C7B7B] tw:!text-[15px] tw:!font-[400] tw:!italic tw:leading-relaxed tw:!mb-0">
                                {t("bookingDetail.cancelBookingSuccessModal.loading")}
                            </p>
                        </div>
                    ) : (
                        <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:mt-6">
                            <div className="tw:mb-2">
                                <ChecklistIcon
                                    color="#3CAEA3"
                                    size={30}
                                />
                            </div>

                            <div className="tw:text-[#282828] tw:text-[18px] tw:font-[600] tw:leading-[25px] tw:font-semibold tw:font-hanken-grotesk tw:mb-1 tw:max-w-[560px]">
                                {t("bookingDetail.cancelBookingSuccessModal.title")}
                            </div>

                            <div className="tw:!grid tw:!w-full tw:!mb-6">
                                {subTitle && (
                                    <p className="tw:!text-[#666666] tw:!text-[15px] tw:!font-[400] tw:!mb-0">
                                        {subTitle}
                                    </p>
                                )}
                            </div>

                            {paymentStatus !== "CHARGE_PENDING" && (
                                <div className="tw:!bg-[#f9f9f9] tw:!rounded-[8px] tw:!p-6 tw:!mb-8 tw:!w-full">
                                    {/* Card customers get a refund line; deferred-payment customers are invoiced instead. */}
                                    {!deferredPayment && (
                                        <div className="tw:!mb-4">
                                            <span className="tw:!text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!items-center tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingSuccessModal.refundAmount")}&nbsp;<span className="tw:!text-[#7C7B7B] tw:!font-[400] tw:!text-[13px]">{t("bookingDetail.cancelBookingSuccessModal.refundAmountNote")}</span></span>
                                            <p className="tw:!text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!flex-wrap tw:!justify-start tw:!text-left">{formatJPY(refundAmount)}&nbsp;<span className="tw:!text-[#7C7B7B] tw:!font-[400] tw:!text-[13px]">{`(= ${getRefundAmountDetail()})`}</span></p>
                                        </div>
                                    )}

                                    <RateFeeRow
                                        withTopBorder={!deferredPayment}
                                        rateLabel={t("bookingDetail.cancelBookingSuccessModal.cancellationRate")}
                                        rateText={formatRate(pickLanguage(desc), cancellationPercent)}
                                        feeLabel={t("bookingDetail.cancelBookingSuccessModal.cancellationFee")}
                                        feeText={formatJPY(cancellationFee)}
                                    />

                                    {meetAssistLegs.map((leg) => (
                                        <RateFeeRow
                                            key={leg.key}
                                            rateLabel={leg.rateLabel}
                                            rateText={leg.rateText}
                                            feeLabel={leg.feeLabel}
                                            feeText={leg.feeText}
                                        />
                                    ))}

                                    <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4">
                                        <a
                                            href={getCancellationPolicyLink(role)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="tw:!underline tw:!decoration-[#7C7B7B] tw:inline-flex tw:items-center tw:gap-2"
                                        >
                                            <InfoIcon
                                                color="#7C7B7B"
                                                size={14}
                                                className="tw:shrink-0"
                                            />
                                            <span className="tw:text-[#7C7B7B] tw:!font-[400] tw:!text-[13px]">
                                                {t("bookingDetail.cancelBookingSuccessModal.cancellationPolicy")}
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className={`tw:!grid tw:!gap-4 tw:!w-full ${paymentStatus === "CHARGE_PENDING" ? "tw:border-t tw:border-[#e0e0e0]" : ""}`}>
                                <p className="tw:!text-[#7C7B7B] tw:!text-[15px] tw:!font-[400] tw:!italic tw:leading-relaxed tw:!mb-0">
                                    {t("bookingDetail.cancelBookingSuccessModal.note")}
                                </p>
                            </div>

                        </div>
                    )
                }
        </MotionModal>
    );
};

export default CancelBookingSuccessModal;